import { isNil, omitBy } from 'lodash'

import { api } from '@marketplace-web/core-api/core-api-client-util'
import { ApiClient, ApiResponse } from '@marketplace-web/core-api/api-client-util'

import { GetInboxConversationsArgs, InboxConversationsResp } from '../types/get-inbox-conversations'
import { GetInboxListItemArgs, InboxListItemResp } from '../types/get-inbox-list-item'
import {
  TranslateConversationArgs,
  TranslateConversationResp,
} from '../types/translate-conversation'
import { DeleteMessageNewArgs, DeletePhotoMessageNewArgs } from '../types/delete-message'
import {
  PostReplyArgs,
  PostReplyNewError,
  PostReplyNewArgs,
  PostReplyNewResp,
} from '../types/post-reply'
import { ConversationResp } from '../types/conversation'
import { SetConversationAsReadArgs } from '../types/set-conversation-as-read'
import {
  CreateItemConversationArgs,
  CreateItemConversationResp,
} from '../types/create-item-conversation'
import { CreateInquiryArgs, CreateInquiryResp } from '../types/create-inquiry'
import { ConversationNewResp, InboxConversationsRespNew } from '../types/conversation-new'

export const getInboxConversations = ({ currentPage, perPage }: GetInboxConversationsArgs) =>
  api.get<InboxConversationsResp>('/inbox', {
    params: omitBy(
      {
        page: currentPage,
        per_page: perPage,
      },
      isNil,
    ),
  })

export const getInboxListItem = ({ conversationId }: GetInboxListItemArgs) =>
  api.get<InboxListItemResp>(`/inbox/${conversationId}`)

export const translateConversation = ({ conversationId, translate }: TranslateConversationArgs) =>
  api.put<TranslateConversationResp>(`/conversations/${conversationId}/translation`, {
    translate,
  })

export const deleteConversationSuspicion = (threadId: number | string) =>
  api.delete(`/msg_threads/${threadId}/suspicion`)

export const deleteConversation = (id: number | string) => api.delete(`/conversations/${id}`)

export const deleteMessage = ({ conversationId, messageId }: DeleteMessageNewArgs) =>
  api.delete(`conversations/${conversationId}/replies/${messageId}`)

export const deletePhotoMessage = ({
  conversationId,
  messageId,
  photoId,
}: DeletePhotoMessageNewArgs) =>
  api.delete(`/conversations/${conversationId}/replies/${messageId}/photos/${photoId}`)

export const deletePhotoSuspicion = (photoId: number) =>
  api.delete(`/user_msg_photos/${photoId}/suspicion`)

export const postReply = ({
  conversationId,
  body,
  photoTempUuids,
  isPersonalDataSharingCheckSkipped,
}: PostReplyArgs) =>
  api.post<ConversationResp, PostReplyNewError>(`conversations/${conversationId}/replies`, {
    reply: {
      body,
      photo_temp_uuids: photoTempUuids,
      is_personal_data_sharing_check_skipped: isPersonalDataSharingCheckSkipped,
    },
  })

export const getConversation = (conversationId: number | string) =>
  api.get<ConversationResp>(`/conversations/${conversationId}`)

export const setConversationAsRead = ({ conversationId }: SetConversationAsReadArgs) =>
  api.put<ApiResponse>(`/conversations/${conversationId}/mark_as_read`)

export const reserveTransactionItems = (transactionId: number, reserved: boolean) =>
  api.put(`/transactions/${transactionId}/reservation`, { reserved })

export const createItemConversationThread = ({
  itemId,
  initiator,
  receiverId,
}: CreateItemConversationArgs) =>
  api.post<CreateItemConversationResp>('/conversations', {
    initiator,
    item_id: itemId,
    opposite_user_id: receiverId,
  })

const REACT_QUERY_CONFIG = {
  // throwing so that @tanstack/react-query can properly capture errors
  throwError: true,
}

const messagingApi = new ApiClient({
  baseURL: '/web/api/messaging',
  headers: { platform: 'web' },
})

export const postReplyNew = ({
  conversationId,
  content,
  isPersonalDataSharingCheckSkipped,
  photoTempUuids,
}: PostReplyNewArgs) =>
  messagingApi.post<PostReplyNewResp>(`/conversations/${conversationId}/replies`, {
    content,
    is_personal_data_sharing_check_skipped: isPersonalDataSharingCheckSkipped,
    photo_temp_uuids: photoTempUuids,
  })

export const setConversationAsReadNew = ({ conversationId }: SetConversationAsReadArgs) =>
  messagingApi.post<ApiResponse>(`/conversations/${conversationId}/mark_as_read`)

export const conversationNew = {
  get: (id: string | number) =>
    messagingApi.get<ConversationNewResp>(`/conversations/${id}`, REACT_QUERY_CONFIG),
  queryKey: 'conversation',
}

export const getConversationNew = (id: string | number) =>
  messagingApi.get<ConversationNewResp>(`/conversations/${id}`)

export const getInboxConversationsNew = (nextCursor?: string | null) =>
  messagingApi.get<InboxConversationsRespNew>('/inbox', {
    params: omitBy(
      {
        next_cursor: nextCursor,
      },
      isNil,
    ),
  })

export const createInquiryNew = ({ itemIds, receiverId }: CreateInquiryArgs) =>
  messagingApi.post<CreateInquiryResp>('/inquiries', {
    item_ids: itemIds,
    receiver_id: receiverId,
  })

export const deleteMessageNew = ({ conversationId, messageId }: DeleteMessageNewArgs) =>
  messagingApi.delete(`/conversations/${conversationId}/replies/${messageId}`)
